/**
 * Usuários e convites da empresa (E9, MT-905): a equipe que o administrador da empresa cuida
 * pelo app.
 *
 * Toda rota exige `?client=<id>` — é a empresa cuja equipe se lê ou se muda, e o nível nela é
 * o que o backend confere (`COMPANY_USERS` `FULL`). Por isso o `clientId` é o primeiro
 * argumento de todas as funções, inclusive as que recebem o id da linha.
 *
 * O token do convite não passa por aqui: nem a criação nem o reenvio o devolvem, e no app o
 * caminho de um convite que não chegou é reenviar (decisão 9 de E9).
 */
import { AxiosError } from 'axios';
import { z } from 'zod';

import { apiClient } from '~/api/client';
import { fetchAllPages } from '~/api/pagination';
import type { components } from '~/types/api';

export type CompanyUser = components['schemas']['CompanyUser'];
export type CompanyInvitation = components['schemas']['CompanyInvitation'];
export type CompanyInvitationCreate = components['schemas']['CompanyInvitationCreateRequest'];
export type CompanyUserUpdate = components['schemas']['PatchedCompanyUserUpdateRequest'];
/** Os níveis que se concedem num vínculo: um por módulo fiscal. */
export type GrantedLevels = components['schemas']['CompanyAccessLevelsRequest'];
/** O que o provedor de e-mail disse do convite (E10): no envio, e depois pelos eventos de entrega. */
export type InvitationDeliveryStatus = components['schemas']['InvitationDeliveryEnum'];

const COMPANY_USERS_URL = '/clients/api/company-users/';
const COMPANY_INVITATIONS_URL = '/clients/api/company-invitations/';

/** Um convite vencido continua aberto e ocupando o e-mail; o caminho dele é reenviar. */
export const INVITATION_EXPIRED_STATUS = 'EXPIRED';

/**
 * A linha que o estado da entrega põe no convite, com os rótulos do portal e do console
 * (`DELIVERY_NOTICE_BY_STATUS`). `UNKNOWN`, `SENT` e `QUEUED` não pedem nada de quem convidou:
 * o convite fica só com o prazo. A tabela cobre o enum inteiro, então um estado novo no schema
 * não compila até ganhar a sua decisão aqui.
 */
export const INVITATION_DELIVERY_LABEL: Record<InvitationDeliveryStatus, string | null> = {
  UNKNOWN: null,
  SENT: null,
  QUEUED: null,
  DELIVERED: 'Entregue',
  BOUNCED: 'E-mail devolvido: confira o endereço',
  COMPLAINED: 'Marcado como spam',
  FAILED: 'Falha na entrega',
};

/** Os estados em tom de alerta, os mesmos que o portal e o console pintam de vermelho. */
export const INVITATION_DELIVERY_PROBLEMS: readonly InvitationDeliveryStatus[] = [
  'BOUNCED',
  'COMPLAINED',
  'FAILED',
];

/** O endereço devolvido não se conserta reenviando: o caminho é revogar e convidar o certo. */
export const INVITATION_BOUNCED_STATUS = 'BOUNCED' satisfies InvitationDeliveryStatus;

export function isInvitationBounced(invitation: CompanyInvitation): boolean {
  return invitation.delivery_status === INVITATION_BOUNCED_STATUS;
}

/**
 * O envio ou o reenvio que devolveu na hora (`email_sent` falso e `BOUNCED`), com a frase do
 * portal e do console (`INVITATION_BOUNCED_MESSAGE`): pedir outro reenvio mandaria ao mesmo
 * endereço errado.
 */
export function invitationBouncedNotice(email: string): string {
  return `O e-mail do convite para ${email} foi devolvido: confira o endereço. O convite continua em aberto; revogue-o para convidar o endereço certo.`;
}

const NETWORK_FAILURE_MESSAGE = 'Falha de conexão. Verifique sua internet.';
const REQUEST_FAILURE_MESSAGE = 'Não foi possível concluir. Tente de novo.';

function companyParams(clientId: number): { client: number } {
  return { client: clientId };
}

/** Os níveis do vínculo na forma que se envia: o que `CompanyAccess` traz dos módulos fiscais. */
export function grantedLevelsOf(access: CompanyUser['access']): GrantedLevels {
  return {
    nfse: access.nfse,
    nfe: access.nfe,
    nfce: access.nfce,
    documents: access.documents,
  };
}

/** A equipe inteira, não só a primeira página: a tela de um vínculo procura a linha nesta lista. */
export function fetchCompanyUsers(clientId: number): Promise<CompanyUser[]> {
  return fetchAllPages<CompanyUser>(COMPANY_USERS_URL, companyParams(clientId));
}

/** Grava papel, níveis ou `is_active` de um vínculo e devolve a linha como a listagem a mostra. */
export async function updateCompanyUser(
  clientId: number,
  id: number,
  body: CompanyUserUpdate,
): Promise<CompanyUser> {
  const response = await apiClient.patch<CompanyUser>(`${COMPANY_USERS_URL}${id}/`, body, {
    params: companyParams(clientId),
  });
  return response.data;
}

export function fetchCompanyInvitations(clientId: number): Promise<CompanyInvitation[]> {
  return fetchAllPages<CompanyInvitation>(COMPANY_INVITATIONS_URL, companyParams(clientId));
}

export async function createCompanyInvitation(
  clientId: number,
  body: CompanyInvitationCreate,
): Promise<CompanyInvitation> {
  const response = await apiClient.post<CompanyInvitation>(COMPANY_INVITATIONS_URL, body, {
    params: companyParams(clientId),
  });
  return response.data;
}

/** Token novo e prazo novo na mesma linha: o link anterior para de valer na hora. */
export async function resendCompanyInvitation(
  clientId: number,
  id: number,
): Promise<CompanyInvitation> {
  const response = await apiClient.post<CompanyInvitation>(
    `${COMPANY_INVITATIONS_URL}${id}/resend/`,
    null,
    { params: companyParams(clientId) },
  );
  return response.data;
}

export async function revokeCompanyInvitation(clientId: number, id: number): Promise<void> {
  await apiClient.delete(`${COMPANY_INVITATIONS_URL}${id}/`, {
    params: companyParams(clientId),
  });
}

/** A recusa como a tela a mostra. */
export interface CompanyTeamRefusal {
  /** A mensagem do topo do formulário; vazia quando só há erro de campo. */
  message: string;
  /** Campo → primeira mensagem: `email`, `role`, `is_active` e um por módulo. */
  fieldErrors: Record<string, string>;
}

const messagesSchema = z.array(z.string());

const codedSchema = z.object({
  code: z.string(),
  message: z.string().catch(''),
});

const byFieldSchema = z.object({
  detail: z.string().optional(),
  non_field_errors: messagesSchema.optional(),
  email: messagesSchema.optional(),
  role: messagesSchema.optional(),
  is_active: messagesSchema.optional(),
  access: z.record(messagesSchema).optional(),
});

type FieldMessages = z.infer<typeof byFieldSchema>;

function firstMessage(messages: string[] | undefined): string | undefined {
  return messages?.[0];
}

function fieldErrorsOf(body: FieldMessages): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of ['email', 'role', 'is_active'] as const) {
    const message = firstMessage(body[field]);
    if (message) {
      errors[field] = message;
    }
  }
  for (const [module, messages] of Object.entries(body.access ?? {})) {
    const message = firstMessage(messages);
    if (message) {
      errors[module] = message;
    }
  }
  return errors;
}

function plainRefusal(message: string): CompanyTeamRefusal {
  return { message, fieldErrors: {} };
}

/**
 * Duas formas de recusa chegam aqui: as do serviço de convite, com código e mensagem
 * (`INVITATION_PENDING` e as outras de E9), e as do DRF, por campo. Das primeiras vale a
 * mensagem, que já diz o que fazer; das segundas, o campo a que cada uma pertence. O corpo é
 * lido por schema e não por asserção de tipo — um proxy no caminho pode devolver qualquer coisa.
 */
export function companyTeamRefusalOf(error: unknown): CompanyTeamRefusal {
  if (!(error instanceof AxiosError)) return plainRefusal(REQUEST_FAILURE_MESSAGE);
  if (!error.response) return plainRefusal(NETWORK_FAILURE_MESSAGE);

  const coded = codedSchema.safeParse(error.response.data);
  if (coded.success) {
    return plainRefusal(coded.data.message || REQUEST_FAILURE_MESSAGE);
  }

  const byField = byFieldSchema.safeParse(error.response.data);
  if (!byField.success) return plainRefusal(REQUEST_FAILURE_MESSAGE);

  const fieldErrors = fieldErrorsOf(byField.data);
  const message = firstMessage(byField.data.non_field_errors) ?? byField.data.detail ?? '';
  if (message || Object.keys(fieldErrors).length > 0) {
    return { message, fieldErrors };
  }
  return plainRefusal(REQUEST_FAILURE_MESSAGE);
}
